import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ExportEvent, ExportProgress } from './export-events';

/**
 * Where a running export currently is.
 *
 * 'waiting' is the honest name for the long part: the server is still running the query
 * and not a single byte has been sent, so no percentage exists yet. Only once bytes start
 * arriving ('downloading') can a real figure be shown, and only if the response carried a
 * Content-Length. 'building' covers turning the JSON into a workbook in the browser.
 */
export type ExportPhase = 'waiting' | 'downloading' | 'building';

export interface ExportJobState {
  key: string;
  label: string;
  phase: ExportPhase;
  loaded: number;
  total: number;
  /** 0-100, or null when the size is unknown and the bar must stay indeterminate. */
  percent: number | null;
  /** Epoch ms, so the UI can show how long the user has been waiting. */
  startedAt: number;
  /** Only true before the response lands — after that, cancelling would waste the work. */
  cancellable: boolean;
}

/**
 * Tracks long-running exports outside the component that started them.
 *
 * The all-product exports can easily outlive the page. Routing away destroys the dashboard
 * component, so a flag kept there resets to false — which left the download button enabled
 * while the request was still in flight, and a second click fired the same expensive query
 * again. Holding the state here, in a root singleton, means the button still reads as busy
 * when the user navigates back, and a bar in the app shell can show progress from any page.
 *
 * The subscription lives here too, so leaving the page no longer cancels the export: the
 * file still downloads when the response finally arrives.
 */
@Injectable({ providedIn: 'root' })
export class ExportJobService {
  private readonly jobs = new Map<string, ExportJobState>();
  private readonly subscriptions = new Map<string, Subscription>();
  private readonly jobsSubject = new BehaviorSubject<ExportJobState[]>([]);

  /** Every job currently in flight, for the progress bar and anything else that cares. */
  readonly jobs$ = this.jobsSubject.asObservable();

  /** True when any of the given job keys is still running. */
  isRunning(...keys: string[]): boolean {
    return keys.some(key => this.jobs.has(key));
  }

  /**
   * Starts `work` unless a job with the same key is already in flight.
   * Returns false when it was already running, so the caller can skip its own start-up
   * instead of duplicating it.
   */
  start(
    key: string,
    label: string,
    work: Observable<ExportEvent>,
    handlers: { next: (body: any) => void; error: (error: any) => void }
  ): boolean {
    if (this.jobs.has(key)) {
      return false;
    }

    this.jobs.set(key, {
      key,
      label,
      phase: 'waiting',
      loaded: 0,
      total: 0,
      percent: null,
      startedAt: Date.now(),
      cancellable: true
    });
    this.emit();

    // Subscribed here rather than in the component so component teardown can't cancel it.
    let settled = false;

    const subscription = work.subscribe({
      next: (event: ExportEvent) => {
        if (event.kind === 'progress') {
          this.applyProgress(key, event.progress);
          return;
        }

        settled = true;
        // The bytes are already paid for; cancelling now would throw away finished work.
        this.patch(key, { phase: 'building', cancellable: false });

        // Building the workbook is synchronous and blocks the UI thread, so the bar would
        // never paint its 'building' state if we called straight into it. One frame's delay
        // lets the browser render before the thread locks up.
        setTimeout(() => {
          try {
            handlers.next(event.body);
          } catch (error) {
            console.error(`Export job "${key}" failed while building its file:`, error);
          } finally {
            this.finish(key);
          }
        }, 50);
      },
      error: (error: any) => {
        settled = true;
        try {
          handlers.error(error);
        } catch (handlerError) {
          console.error(`Export job "${key}" failed while handling its error:`, handlerError);
        } finally {
          this.finish(key);
        }
      },
      // Guards the case where the stream ends without ever delivering a response, which
      // would otherwise leave the key stuck as running and the button disabled forever.
      complete: () => {
        if (!settled) {
          this.finish(key);
        }
      }
    });

    // subscribe() has already returned by now, so a stream that failed synchronously would
    // have finished the job before there was anything to store. Storing the subscription
    // then would strand a dead entry in the map that nothing ever clears.
    if (this.jobs.has(key)) {
      this.subscriptions.set(key, subscription);
    } else {
      subscription.unsubscribe();
    }

    return true;
  }

  /**
   * Abandons a running export. Unsubscribing aborts the underlying HTTP request, which
   * matters here: without it a user who started the wrong export has no way to stop it
   * and must wait out the whole thing before the button frees up again.
   */
  cancel(key: string) {
    const job = this.jobs.get(key);
    if (!job || !job.cancellable) {
      return;
    }
    this.finish(key);
  }

  private applyProgress(key: string, progress: ExportProgress) {
    this.patch(key, {
      phase: 'downloading',
      loaded: progress.loaded,
      total: progress.total,
      percent: progress.total > 0
        ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
        : null
    });
  }

  private patch(key: string, changes: Partial<ExportJobState>) {
    const job = this.jobs.get(key);
    if (!job) {
      return;
    }
    this.jobs.set(key, { ...job, ...changes });
    this.emit();
  }

  private finish(key: string) {
    this.subscriptions.get(key)?.unsubscribe();
    this.subscriptions.delete(key);
    if (this.jobs.delete(key)) {
      this.emit();
    }
  }

  private emit() {
    this.jobsSubject.next(Array.from(this.jobs.values()));
  }
}
